import BaseComponent from './BaseComponent';
import { gsap } from 'gsap';

export default class MenuDrawer extends BaseComponent {
	constructor(drawer, menu) {
		super();

		this.elm = drawer;
		this.menu = menu;

		this.breakpoint = this.menu.breakpoint;

		this.id = this.elm.getAttribute('data-drawer');
		this.hasDrawer = this.menu.elm.querySelector('[data-has-drawer="' + this.id + '"]');
		this.button = this.hasDrawer.querySelector('[data-drawer-button]');
		this.closeButton = this.elm.querySelector('[data-drawer-close]');
		this.container = this.elm.querySelector('[data-drawer-container]');
		this.background = this.elm.querySelector('[data-drawer-background]');
		this.links = this.elm.querySelectorAll('a');

		this.setInitialState();

		// Listeners
		this.breakpoint.addEventListener('change', () => {
			this.setInitialState();
		});

		this.button.addEventListener('click', (e) => {
			e.preventDefault();
			if (this.elm.classList.contains('is-open')) {
				this.close();
			} else {
				this.open();
			}
		});

		this.closeButton.addEventListener('click', (e) => {
			e.preventDefault();
			this.close();
		});

		this.isHoveringHasDrawer = false;
		this.isHoveringDrawer = false;

		this.hasDrawer.addEventListener('mouseenter', () => {
			this.isHoveringHasDrawer = true;
			this.checkMouseEnter();
		});
		this.elm.addEventListener('mouseenter', () => {
			this.isHoveringDrawer = true;
			this.checkMouseEnter();
		});

		this.hasDrawer.addEventListener('mouseleave', () => {
			this.isHoveringHasDrawer = false;
			this.checkMouseLeave();
		});
		this.elm.addEventListener('mouseleave', () => {
			this.isHoveringDrawer = false;
			this.checkMouseLeave();
		});

		this.links.forEach((link) => {
			link.addEventListener('click', (e) => {
				this.menu.cleanupLinks();
				const drawerId = link.closest('[data-drawer]').getAttribute('data-drawer');
				this.menu.elm.querySelector('[data-has-drawer="' + drawerId + '"]').classList.add('is-active');
			});
		});
	}

	checkMouseEnter() {
		if (this.isHoveringHasDrawer || this.isHoveringDrawer) {
			if (this.breakpoint.matches) {
				this.open();
			}
		}
	}

	checkMouseLeave() {
		if (!this.isHoveringHasDrawer && !this.isHoveringDrawer) {
			if (this.breakpoint.matches) {
				this.close();
			}
		}
	}

	setInitialState() {
		if (this.breakpoint.matches) {
			gsap.set(this.elm, {
				autoAlpha: 0,
				scale: 1,
				xPercent: 0,
			});
			gsap.set(this.background, {
				scaleY: 0,
			});
			gsap.set(this.container, {
				autoAlpha: 0,
			});
		} else {
			gsap.set(this.elm, {
				autoAlpha: 0,
				scale: 1,
				xPercent: 0,
			});
			gsap.set(this.background, {
				scaleY: 1,
			});
			gsap.set(this.container, {
				autoAlpha: 1,
			});
		}
		this.createTimelines();
		this.elm.classList.remove('is-open');
		this.hasDrawer.classList.remove('is-open');
		this.tl.seek('initial');
	}

	createTimelines() {
		this.tl = gsap.timeline({
			paused: true,
		});

		this.tl.addLabel('initial', 0);
		this.tl.set(this.elm, {
			autoAlpha: 1,
		});

		if (this.breakpoint.matches) {
			this.createTimelineDesktop();
		} else {
			this.createTimelineMobile();
		}

		this.tl.set(this.elm, {
			autoAlpha: 0,
		});
		this.tl.addLabel('closed');
	}

	createTimelineMobile() {
		this.tl.to(
			this.elm,
			{
				xPercent: -100,
				duration: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.6,
				delay: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.1,
				ease: 'power2.inOut',
			},
			0
		);

		this.tl.addLabel('open');

		this.tl.to(
			this.elm,
			{
				xPercent: 0,
				duration: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.6,
				delay: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.1,
				ease: 'power2.inOut',
			},
			'open'
		);
	}

	createTimelineDesktop() {
		this.tl.to(this.background, {
			scaleY: 1,
			duration: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.3,
			ease: 'power2.out',
		});

		this.tl.to(
			this.container,
			{
				autoAlpha: 1,
				duration: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.15,
				delay: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.15,
				ease: 'none',
			},
			0
		);

		this.tl.addLabel('open');

		this.tl.to(
			this.container,
			{
				autoAlpha: 0,
				duration: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.1,
				ease: 'none',
			},
			'open'
		);

		this.tl.to(
			this.background,
			{
				scaleY: 0,
				duration: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.3,
				ease: 'power2.out',
			},
			'open'
		);
	}

	open() {
		this.menu.trapFocus(this.elm, this.button);

		this.menu.drawers.forEach((drawer) => {
			drawer.close();
		});

		gsap.set(this.elm, {
			pointerEvents: 'all',
			zIndex: 1000,
		});

		this.button.setAttribute('aria-expanded', 'true');

		if (this.breakpoint.matches) {
			this.menu.header.showBlur();
		} else {
			this.menu.openDrawer();
		}
		this.elm.classList.add('is-open');
		this.hasDrawer.classList.add('is-open');

		this.tl.tweenTo('open');
	}

	close() {
		if (!this.elm.classList.contains('is-open')) {
			return;
		}
		this.menu.header.hideBlur();

		gsap.set(this.elm, {
			pointerEvents: 'none',
			zIndex: 10,
		});

		this.button.setAttribute('aria-expanded', 'false');

		if (!this.breakpoint.matches) {
			setTimeout(() => {
				this.menu.closeDrawer();
			}, 300);
		} else {
			this.menu.removeTrapFocus();
		}

		this.tl.tweenTo('closed', {
			onComplete: () => {
				this.setInitialState();
			},
		});
	}

	closeMenu() {
		if (!this.breakpoint.matches) {
			gsap.to(this.elm, {
				autoAlpha: 0,
				scale: 0.9,
				duration: this.menu.reduceMotionMediaQuery.matches ? 0.01 : 0.6,
				ease: 'power2.out',
				onComplete: () => {
					this.setInitialState();
				},
			});
		} else {
			this.close();
		}
	}
}
